// 14-axis radar of section means, inline SVG (no chart library, by design).
// The radar is a shape to read together, not a ranking: rings mark the 1-4
// scale, the polygon joins each answered section's mean.

import type { JSX } from 'react';
import { stripInlineMarkup } from '@cca/parishes-shared';
import type { SectionStat } from '../api';

const SIZE = 420;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R = 150;
const SCALE_MAX = 4;

function point(angle: number, radius: number): { x: number; y: number } {
  return { x: CX + radius * Math.sin(angle), y: CY - radius * Math.cos(angle) };
}

export function SectionRadar({ sections }: { sections: SectionStat[] }): JSX.Element {
  const n = sections.length;
  const angleFor = (i: number): number => (2 * Math.PI * i) / n;
  const radiusFor = (mean: number): number =>
    (Math.max(0, Math.min(mean, SCALE_MAX)) / SCALE_MAX) * R;

  // Polygon through answered sections only; a gap is left where a section has
  // no answers yet (never imputed).
  const answered = sections.map((s, i) => ({ s, i })).filter(({ s }) => s.mean !== null);
  const polygonPoints = answered
    .map(({ s, i }) => {
      const { x, y } = point(angleFor(i), radiusFor(s.mean ?? 0));
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      role="img"
      aria-label={`Overview of section responses on the 1 to 4 scale: ${answered
        .map(({ s }) => `${stripInlineMarkup(s.title)} ${s.mean}`)
        .join('; ')}`}
      style={{ width: '100%', maxWidth: 460, height: 'auto', display: 'block' }}
    >
      {/* Rings at 1, 2, 3, 4 */}
      {[1, 2, 3, 4].map((ring) => (
        <circle
          key={ring}
          cx={CX}
          cy={CY}
          r={(ring / SCALE_MAX) * R}
          fill="none"
          stroke="#e5e0d5"
          strokeWidth={ring === 4 ? 1.5 : 1}
        />
      ))}
      {/* Ring value labels, up the top axis */}
      {[1, 2, 3, 4].map((ring) => (
        <text
          key={`label-${ring}`}
          x={CX + 4}
          y={CY - (ring / SCALE_MAX) * R + 11}
          fontSize={9}
          fill="#6b7280"
        >
          {ring}
        </text>
      ))}
      {/* Axes and numbered section badges — the same key as the score table
          beside the chart, so a spoke can be found by its number. */}
      {sections.map((s, i) => {
        const angle = angleFor(i);
        const end = point(angle, R);
        const label = point(angle, R + 18);
        return (
          <g key={s.section_no}>
            <line x1={CX} y1={CY} x2={end.x} y2={end.y} stroke="#efece3" strokeWidth={1} />
            <circle
              cx={label.x}
              cy={label.y}
              r={9.5}
              fill="var(--health-accent-lt, #eef3e3)"
              stroke="var(--health-accent, #5c7e25)"
              strokeWidth={1}
            />
            <text
              x={label.x}
              y={label.y + 3.6}
              fontSize={10.5}
              fontWeight={700}
              fill="var(--health-accent-dk, #46621b)"
              textAnchor="middle"
            >
              {s.section_no}
            </text>
          </g>
        );
      })}
      {/* The shape */}
      {answered.length >= 3 && (
        <polygon
          points={polygonPoints}
          fill="rgba(0, 84, 135, 0.16)"
          stroke="#005487"
          strokeWidth={2}
          strokeLinejoin="round"
        />
      )}
      {/* Vertices */}
      {answered.map(({ s, i }) => {
        const { x, y } = point(angleFor(i), radiusFor(s.mean ?? 0));
        return (
          <circle key={`v-${s.section_no}`} cx={x} cy={y} r={3.5} fill="#005487">
            <title>{`${s.section_no}. ${stripInlineMarkup(s.title)}: ${s.mean} on the 1 to 4 scale`}</title>
          </circle>
        );
      })}
    </svg>
  );
}
